import React, { useState, useEffect, useCallback } from 'react';
import { Search, RefreshCw } from 'lucide-react';
import { Eye } from 'lucide-react';
import axios from 'axios';
import AddUserModal from './Addusermodal';
import EditUserModal from './Editusermodal';
import ViewUserModal from './Viewusermodal';
import DeleteUserModal from './Deleteusermodal';

const API_URL = window.location.origin.includes("192.168")
  ? "http://192.168.1.101:5010/api/"
  : "http://localhost:5010/api/";

function formatBirthday(dobString) {
  if (!dobString) return '—';
  const d = new Date(dobString);
  if (isNaN(d)) return dobString;

  const day = d.getDate();
  const month = d.toLocaleString('en-GB', { month: 'short' });
  const year = d.getFullYear();

  return `${day} ${month}, ${year}`;
}

function mapDriver(d) {
  return {
    id: d.serialNumber || d.id,
    _mongoId: d.id,
    name: d.name || d.callsign || 'Unknown',
    callsign: d.callsign || '—',
    email: d.email || '—',
    birthday: d.birthday || '',
    driverClass: d.driverClass || '',
    status: d.emailVerified
      ? (d.status === 'suspended' ? 'Blocked' : 'Active')
      : 'Unverified',
    newsletterOptIn: d.newsletterOptIn,
    joined: d.registeredAt
      ? new Date(d.registeredAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : '—',
  };
}

const CLASS_LABEL = { '': 'ROOKIE', zero: 'ROOKIE', some: 'DRIVER', vet: 'VETERAN' };
const LIMIT = 20;

function User() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ active: 0, unverified: 0, blocked: 0 });

  const [showAdd, setShowAdd] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [viewUser, setViewUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);

  const fetchDrivers = useCallback(async (pg = 1) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API_URL}`, {
        params: { page: pg, limit: LIMIT },
      });
      if (data.status) {
        setUsers(data.drivers.map(mapDriver));
        setTotal(data.total);
        setCounts(data.counts || { active: 0, unverified: 0, blocked: 0 });
      } else {
        setError(data.message || 'Failed to load drivers.');
      }
    } catch (err) {
      console.error('[User] fetch error:', err);
      setError(err.response?.data?.message || 'Cannot connect to server.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDrivers(page); }, [page, fetchDrivers]);

  const handleFilterChange = (f) => { setFilter(f); setPage(1); };

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    const matchSearch =
      u.name.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      u.callsign.toLowerCase().includes(q) ||
      u.id.toLowerCase().includes(q);
    const matchFilter = filter === 'All' || u.status === filter;
    return matchSearch && matchFilter;
  });

  const totalPages = Math.ceil(total / LIMIT);

  const filterCounts = {
    All: total,
    Active: counts.active,
    Unverified: counts.unverified,
    Blocked: counts.blocked,
  };

  const handleAdd = () => fetchDrivers(page);
  const handleSave = (updated) => setUsers(prev => prev.map(u => u.id === updated.id ? updated : u));
  const handleDelete = (id) => { setUsers(prev => prev.filter(u => u.id !== id)); setTotal(t => t - 1); };

  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
    .reduce((acc, p, idx, arr) => {
      if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
      acc.push(p);
      return acc;
    }, []);

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Users</h1>
          <p className="page-subtitle">All registered Vegas Karts Users from the website.</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            className="btn-secondary"
            onClick={() => fetchDrivers(page)}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', marginBottom: 24 }}>
        {[
          { label: 'Total Users',      value: total },
          { label: 'Active Users',     value: counts.active },
          { label: 'Unverified Users', value: counts.unverified },
          { label: 'Blocked Users',    value: counts.blocked },
        ].map(s => (
          <div className="stat-card" key={s.label}>
            <div className="stat-info">
              <div className="stat-label">{s.label}</div>
              <div className="stat-value">{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        {/* Search + Filter */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 20 }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              type="text"
              placeholder="Search name, email, callsign, serial..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="form-control"
              style={{ paddingLeft: 36 }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {['All', 'Active', 'Unverified', 'Blocked'].map(f => (
              <button
                key={f}
                onClick={() => handleFilterChange(f)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '7px 14px', borderRadius: 8, border: '1px solid',
                  borderColor: filter === f ? 'var(--accent-red)' : 'var(--border)',
                  background: filter === f ? 'rgba(232,0,29,0.12)' : 'transparent',
                  color: filter === f ? 'var(--text-primary)' : 'var(--text-muted)',
                  cursor: 'pointer', fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, fontSize: 13,
                  transition: 'all 0.2s',
                }}
              >
                {f}
                <span style={{
                  background: filter === f ? 'rgba(232,0,29,0.25)' : 'rgba(255,255,255,0.06)',
                  color: filter === f ? '#ff6b6b' : 'var(--text-muted)',
                  borderRadius: 10, padding: '1px 7px', fontSize: 11,
                  fontFamily: 'JetBrains Mono, monospace',
                }}>
                  {filterCounts[f] ?? 0}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
            <div style={{ fontSize: 13, fontFamily: 'JetBrains Mono, monospace', letterSpacing: 2 }}>LOADING DRIVERS...</div>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <div style={{ color: 'var(--accent-red)', fontSize: 13, marginBottom: 12 }}>{error}</div>
            <button className="btn-secondary" onClick={() => fetchDrivers(page)}>Try Again</button>
          </div>
        )}

        {/* Table */}
        {!loading && !error && (
          <div className="table-wrapper" style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Serial No.</th>
                  <th>Name</th>
                  <th>Callsign</th>
                  <th>Email</th>
                  <th>Birth Date</th>
                  <th>Class</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.id}>
                    <td style={{ color: 'var(--accent-red)', fontFamily: 'JetBrains Mono, monospace', fontWeight: 700 }}>
                      {u.id}
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 30, height: 30, borderRadius: '50%',
                          background: 'var(--accent-gradient)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 12, color: '#fff', flexShrink: 0,
                        }}>
                          {(u.name || '?')[0].toUpperCase()}
                        </div>
                        {u.name}
                      </div>
                    </td>
                    <td style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: 'var(--text-muted)' }}>
                      {u.callsign}
                    </td>
                    <td style={{ color: 'var(--text-muted)', minWidth: 160, wordBreak: 'break-all' }}>
                      {u.email}
                    </td>

                    {/* Birthday column */}
                    <td style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {formatBirthday(u.birthday)}
                    </td>

                    <td>
                      <span style={{
                        fontSize: 11, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace',
                        letterSpacing: 1, color: 'var(--text-muted)',
                      }}>
                        {CLASS_LABEL[u.driverClass] || 'ROOKIE'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${
                        u.status === 'Active'  ? 'badge-success' :
                        u.status === 'Blocked' ? 'badge-warning' : 'badge-danger'
                      }`}>
                        {u.status}
                      </span>
                    </td>
                    <td style={{ color: 'var(--text-muted)' }}>{formatBirthday(u.joined)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          onClick={() => setViewUser(u)}
                          style={{ background: 'rgba(99,102,241,0.1)', border: 'none', borderRadius: 6, padding: '5px 8px', color: '#818cf8', cursor: 'pointer' }}
                          title="View"
                        >
                          <Eye size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>
                      No drivers found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && !error && totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, flexWrap: 'wrap', gap: 12 }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Page {page} of {totalPages} · {total} total drivers
            </span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  width: 32, height: 32, borderRadius: 6, border: '1px solid var(--border)',
                  background: 'transparent',
                  color: page === 1 ? 'var(--text-muted)' : 'var(--text-secondary)',
                  cursor: page === 1 ? 'not-allowed' : 'pointer',
                  fontSize: 14, opacity: page === 1 ? 0.4 : 1,
                }}>‹</button>

              {pageNumbers.map((p, idx) =>
                p === '...' ? (
                  <span key={`e-${idx}`} style={{
                    width: 32, height: 32, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', color: 'var(--text-muted)', fontSize: 13,
                  }}>…</span>
                ) : (
                  <button key={p} onClick={() => setPage(p)} style={{
                    width: 32, height: 32, borderRadius: 6, border: '1px solid',
                    borderColor: p === page ? 'var(--accent-red)' : 'var(--border)',
                    background: p === page ? 'rgba(232,0,29,0.12)' : 'transparent',
                    color: p === page ? 'var(--text-primary)' : 'var(--text-muted)',
                    cursor: 'pointer', fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 13,
                  }}>{p}</button>
                )
              )}

              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{
                  width: 32, height: 32, borderRadius: 6, border: '1px solid var(--border)',
                  background: 'transparent',
                  color: page === totalPages ? 'var(--text-muted)' : 'var(--text-secondary)',
                  cursor: page === totalPages ? 'not-allowed' : 'pointer',
                  fontSize: 14, opacity: page === totalPages ? 0.4 : 1,
                }}>›</button>
            </div>
          </div>
        )}
      </div>

      {showAdd    && <AddUserModal    onClose={() => setShowAdd(false)}   onAdd={handleAdd} />}
      {editUser   && <EditUserModal   user={editUser}   onClose={() => setEditUser(null)}   onSave={handleSave} />}
      {viewUser   && <ViewUserModal   user={viewUser}   onClose={() => setViewUser(null)} />}
      {deleteUser && <DeleteUserModal user={deleteUser} onClose={() => setDeleteUser(null)} onDelete={handleDelete} />}
    </div>
  );
}

export default User;